import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Button } from "@typedoc/ui";
import { idSchema } from "@typedoc/contracts/ids";
import { z } from "zod";
import { ScopeNotice } from "../../../components/connections/scope-notice";
import { ErrorNotice } from "../../../components/error-notice";
import { WaitActions, WaitDetail, WaitMark, useSlow } from "../../../components/wait-screen";
import { normalizeAppError, type AppError } from "../../../lib/app-error";
import { completeAttempt } from "../../../lib/connections/api";
import { announceAttempt } from "../../../lib/connections/attempt-signal";
import {
  forgetAttemptWorkspace,
  rememberedAttemptWorkspace,
} from "../../../lib/connections/attempt-workspace";
import { selectWorkspaceMutationOptions } from "../../../lib/mutations/workspaces";
import { workspacesQueryOptions } from "../../../lib/queries/workspaces";
import { differentAccountCopy } from "../../../components/connections/sign-in-steps";
import { useConnectionsScope } from "../../../lib/connections/use-connections-scope";

export const Route = createFileRoute("/_authenticated/_workspace/connections_/callback")({
  // The provider appends its own status and account parameters. They are not proof of anything,
  // so they pass through unread and the server is asked instead.
  validateSearch: z.looseObject({ attempt: idSchema("attempt") }),
  component: ConnectionCallback,
});

/** Neither the app nor the account is known here, so the wording names neither. */
const differentAccount = differentAccountCopy();

/** How long to wait before asking again about an attempt that is still settling. */
const SETTLING_POLL_MS = 1500;

/** What this window shows once the attempt has settled. */
type Outcome = "working" | "handed-back" | "not-finished" | "different-account";

/**
 * Where the provider's sign-in page returns a browser sign-in (a desktop one returns to
 * `/connections/finished`). It settles the attempt, then tells the page that is waiting and
 * closes. With nobody waiting (a fresh tab) a new account opens Connections on the add dialog's
 * last step and a renewed one opens its own page; a sign-in that failed or expired says so here
 * rather than leaving the person on an unrelated page.
 *
 * An attempt belongs to the workspace it began in. If the person has switched workspace since,
 * completing here would ask the wrong workspace (and perhaps the wrong region), so the window
 * says so and offers to switch back first; completion waits until that workspace is active.
 */
function ConnectionCallback() {
  const { attempt } = Route.useSearch();
  const { context, blocker } = useConnectionsScope();
  const navigate = useNavigate();
  const [error, setError] = useState<AppError | null>(null);
  const [outcome, setOutcome] = useState<Outcome>("working");
  const started = useRef(false);
  // Each completion is a run; a retry starts the next, and the one it replaces stops acting.
  const current = useRef(0);
  // Leaving the page ends the run: nothing it was waiting for may act on a page the person left.
  const mounted = useRef(false);
  const pause = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(pause.current);
    };
  }, []);
  const [run, setRun] = useState(0);
  const [since, setSince] = useState(() => performance.now());
  const slow = useSlow(since);
  const queryClient = useQueryClient();
  const userId = context?.scope.userId ?? "";
  const listing = useQuery({ ...workspacesQueryOptions(userId), enabled: Boolean(userId) });
  const [beganIn] = useState(() => rememberedAttemptWorkspace(attempt));
  const elsewhere = context && beganIn && beganIn !== context.scope.workspaceId ? beganIn : null;
  const select = useMutation({
    ...selectWorkspaceMutationOptions(),
    // As the switcher does: what was verified for the previous workspace must not outlive it.
    onSettled: () => queryClient.removeQueries({ queryKey: ["workspace-access", userId] }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["workspaces", userId] }),
  });

  useEffect(() => {
    if (!context || elsewhere || started.current) return;
    started.current = true;
    const mine = run;
    current.current = run;
    // Ends the moment a retry replaces this run or the page is left, wherever it is waiting.
    const replaced = () => mine !== current.current || !mounted.current;
    async function settle(session: NonNullable<typeof context>) {
      const result = await completeAttempt(session, attempt);
      if (replaced()) return;
      forgetAttemptWorkspace(attempt);
      const acknowledged = await announceAttempt({
        type: "abacor:connection-attempt",
        attemptId: attempt,
        status: result.status,
      });
      if (replaced()) return;
      if (acknowledged) {
        setOutcome("handed-back");
        window.close();
        return;
      }
      // Still settling (this request, or one a retry replaced, got there first): the sign-in has
      // not failed, so ask again rather than say it did. The service ends the attempt itself when
      // it expires, which is what stops this.
      if (result.status === "waiting") {
        await new Promise((resolve) => {
          pause.current = setTimeout(resolve, SETTLING_POLL_MS);
        });
        if (!replaced()) await settle(session);
        return;
      }
      if (result.status !== "connected" || !result.connectionId) {
        setOutcome(result.reason === "different_account" ? "different-account" : "not-finished");
        return;
      }
      // A renewal has nothing left to set up, so it opens the connection it renewed.
      await (result.renewal
        ? navigate({
            to: "/connections/$connectionId",
            params: { connectionId: result.connectionId },
            replace: true,
          })
        : navigate({
            to: "/connections",
            search: { finished: result.connectionId },
            replace: true,
          }));
    }
    settle(context).catch((caught: unknown) => {
      if (!replaced()) setError(normalizeAppError(caught));
    });
  }, [attempt, context, elsewhere, navigate, run]);

  // Asking again is safe: completing an attempt only reads where the provider's sign-in stands.
  function tryAgain() {
    started.current = false;
    setSince(performance.now());
    setRun((n) => n + 1);
  }

  if (elsewhere) {
    const named = (id: string | undefined) =>
      listing.data?.workspaces.find((workspace) => workspace.id === id)?.name;
    const target = named(elsewhere) ?? "the workspace it began in";
    const current = named(context?.scope.workspaceId);
    return (
      <section
        className="connections-surface mx-auto max-w-md px-4 py-20 text-center"
        aria-live="polite"
      >
        <h1>This sign-in began in another workspace</h1>
        <p className="mt-2 text-[13px]/5 text-muted-foreground">
          {current ? `${current} is open now. ` : ""}
          To finish connecting, switch back to {target}.
        </p>
        <ErrorNotice
          error={select.error ? normalizeAppError(select.error) : null}
          className="mt-5 text-left"
        />
        <Button
          className="mt-5"
          disabled={select.isPending || select.isSuccess}
          onClick={() => select.mutate(elsewhere)}
        >
          {select.isPending || select.isSuccess ? "Switching…" : `Switch to ${target} and finish`}
        </Button>
      </section>
    );
  }

  const settled = error !== null || blocker !== null || outcome !== "working";
  const waiting = !settled;
  const detail =
    outcome === "handed-back"
      ? "You can close this window and carry on where you started."
      : outcome === "not-finished"
        ? "You can close this window and try again."
        : outcome === "different-account"
          ? differentAccount.description
          : "";
  return (
    <section className="wait-screen min-h-[calc(100dvh-18px)] px-4 py-20" aria-live="polite">
      <WaitMark still={settled} />
      <h1 className="wait-title">
        {error || blocker
          ? "We couldn't finish connecting"
          : outcome === "handed-back"
            ? "You're connected"
            : outcome === "different-account"
              ? differentAccount.title
              : outcome === "not-finished"
                ? "That sign-in didn't finish"
                : "Finishing the connection…"}
      </h1>
      {waiting ? (
        <WaitDetail slow={slow} note="Confirming your sign-in" />
      ) : (
        detail && <p className="wait-detail">{detail}</p>
      )}
      <WaitActions slow={slow && waiting}>
        <Button variant="outline" size="sm" onClick={tryAgain}>
          Try again
        </Button>
        <Link to="/connections" className="wait-link">
          Back to Connections
        </Link>
      </WaitActions>
      <ErrorNotice error={error} className="mt-5 w-full max-w-md text-left" />
      {blocker && <ScopeNotice blocker={blocker} className="mt-5 w-full max-w-md text-left" />}
      {(error || outcome === "not-finished" || outcome === "different-account") && (
        <Link to="/connections" className="wait-link mt-5">
          Back to Connections
        </Link>
      )}
    </section>
  );
}
